import React from 'react';

/**
 * Table displaying annual breakdown of Funding vs Costs
 */
export default function CostBreakdownTable({ yearlyResults, config }) {
    if (!yearlyResults || yearlyResults.length === 0) return null;

    // Filter to active years
    const activeData = yearlyResults.filter(d => d.year >= config.startYear);

    // Formatter helpers
    const fmt = (val) => val ? `$${(val / 1e9).toFixed(1)}B` : '-';
    const fmtPct = (val) => val ? `${(val * 100).toFixed(1)}%` : '-';

    return (
        <div className="financial-table-wrapper">
            <div className="table-scroll-area">
                <table className="financial-data-table">
                    <thead>
                        <tr className="table-header-row">
                            <th>Year</th>
                            <th className="th-funding">Total Funding</th>
                            <th className="th-muted">Payroll</th>
                            <th className="th-muted">Indiv.</th>
                            <th className="th-muted">Federal</th>
                            <th className="th-muted">State</th>
                            <th className="th-amber" title="Authorized borrowing (positive) and repayment (negative)">Gen. Fund</th>
                            <th className="th-cost" title="SAFECare trust fund spending (replaces Medicare, Medicaid, etc.)">SAFECare Govt</th>
                            <th className="th-baseline th-compare" title="Federal health outlays under current system (CBO) – comparable to SAFECare program">Baseline Govt</th>
                            <th className="th-nhe-ref" title="Total national health spending (all payers) – reference only; not comparable to program cost">Total NHE (ref)</th>
                            <th className="th-muted" style={{ color: '#888' }}>Claims</th>
                            <th className="th-muted">Admin</th>
                            <th className="th-muted">Support</th>
                            <th className="th-muted">Redemption</th>
                            <th className="th-amber">Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activeData.map(d => {
                            const payroll = d.revenueEmployer + d.revenueSelfEmployed;
                            const claims = d.costProvider + d.costDrug;
                            const support = d.costTransition + d.costTuitionSupport + d.costInnovatorSupport;

                            // Include Gen Fund Transfer in "Total Funding" for display purposes
                            const totalFundingDisplay = d.totalRevenue + (d.revenueGenFund || 0);

                            const balance = totalFundingDisplay - d.totalSpending;
                            const isSurplus = balance >= 0;

                            return (
                                <tr key={d.year} className="table-data-row">
                                    <td className="td-year">{d.year}</td>

                                    {/* Funding */}
                                    <td className="td-funding">
                                        {fmt(d.totalRevenue + (d.revenueGenFund || 0))}
                                    </td>
                                    <td className="td-muted">{fmt(payroll)}</td>
                                    <td className="td-muted">{fmt(d.revenueIndividual)}</td>
                                    <td className="td-muted">{fmt(d.revenueFederalRedirect)}</td>
                                    <td className="td-muted">{fmt(d.revenueStateRedirect)}</td>
                                    {/* General Fund Column */}
                                    <td className={`td-gen-fund ${(d.revenueGenFund || 0) < 0 ? 'positive' : 'warning'}`}
                                        title={d.generalFundDebt ? `Outstanding Debt: $${(d.generalFundDebt / 1e9).toFixed(1)}B` : 'No Outstanding Debt'}>
                                        {d.revenueGenFund ? ((d.revenueGenFund > 0 ? '+' : '') + fmt(d.revenueGenFund)) : '-'}
                                    </td>

                                    {/* Cost: Govt comparison (apples-to-apples) + NHE reference */}
                                    <td className="td-cost">
                                        {fmt(d.totalSpending)}
                                    </td>
                                    <td className="td-baseline td-compare" title="CBO federal health outlays (current system)">
                                        {fmt(d.baselineGovtHealthSpending)}
                                    </td>
                                    <td className="td-nhe-ref" title="Total NHE – all payers (reference only)">
                                        {fmt(d.baselineTotalNHE)}
                                    </td>
                                    <td className="td-muted" style={{ color: '#aaa' }} title="Provider, Drug, Dental, Vision, LTSS">
                                        {fmt(d.costProvider + d.costDrug + (d.costDental || 0) + (d.costVision || 0) + (d.costLTSS || 0))}
                                    </td>
                                    <td className="td-muted">{fmt(d.costAdmin)}</td>
                                    <td className="td-muted" title="Safety Net, Capital, Tuition, Innovator">
                                        {fmt((d.costTransition || 0) + (d.costTuitionSupport || 0) + (d.costInnovatorSupport || 0) + (d.costSafetyNet || 0) + (d.costCapitalGrants || 0))}
                                    </td>
                                    <td className="td-muted">{fmt(d.costDebtRedemption)}</td>

                                    {/* Balance */}
                                    <td className={`td-balance ${isSurplus ? 'positive' : 'negative'}`}>
                                        {isSurplus ? '+' : ''}{fmt(balance)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
